import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { TrendingUp, TrendingDown, AlertCircle, Upload, Zap, ArrowRight } from "lucide-react";
import { API } from "../App";

function fmt(n) {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 2,
  }).format(n || 0);
}

function formatDate(s) {
  if (!s) return "";
  const [y, m, d] = s.slice(0, 10).split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${parseInt(d)} ${months[parseInt(m) - 1]} ${y}`;
}

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [recent, setRecent]   = useState([]);
  const [loading, setLoading] = useState(true);

  const token   = localStorage.getItem("taxbuddy_token");
  const headers = { Authorization: `Bearer ${token}` };
  const user    = JSON.parse(localStorage.getItem("taxbuddy_user") || "{}");
  const firstName = (user.name || user.full_name || "there").split(" ")[0];

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/summary`, { headers }),
      axios.get(`${API}/api/transactions?page=1&limit=5&sort=date&order=desc`, { headers }),
    ])
      .then(([sumRes, txRes]) => {
        setSummary(sumRes.data);
        setRecent(txRes.data.transactions || txRes.data.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const daysToDeadline = () => {
    const now      = new Date();
    const deadline = new Date(now.getFullYear(), 6, 1); // Jul 1
    if (now >= deadline) deadline.setFullYear(deadline.getFullYear() + 1);
    return Math.ceil((deadline - now) / 86400000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
        Loading…
      </div>
    );
  }

  const totalIncome     = summary?.total_income      || 0;
  const totalExpenses   = summary?.total_expenses    || 0;
  const totalDeductions = summary?.total_deductions  || summary?.total_deductible || 0;
  const uncategorised   = summary?.uncategorized_count || 0;
  const txCount         = summary?.transaction_count  || 0;

  const days = daysToDeadline();

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">G'day, {firstName} 👋</h1>
          <p className="text-gray-500 text-sm mt-1">
            Here's your tax summary for this financial year.
          </p>
        </div>
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
          <AlertCircle size={15} className="text-amber-500 flex-shrink-0" />
          <span className="text-amber-700 text-sm font-semibold">
            {days} days until end of financial year
          </span>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Income */}
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <TrendingUp size={16} className="text-green-600" />
            </div>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Income</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{fmt(totalIncome)}</p>
          <p className="text-xs text-gray-400 mt-1">{txCount} total transactions</p>
        </div>

        {/* Expenses */}
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <TrendingDown size={16} className="text-red-500" />
            </div>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Expenses</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{fmt(totalExpenses)}</p>
          <p className="text-xs text-gray-400 mt-1">this financial year</p>
        </div>

        {/* Deductions */}
        <div className="bg-white border border-primary/20 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <span className="text-primary text-xs font-bold">ATO</span>
            </div>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Deductions</span>
          </div>
          <p className="text-2xl font-bold text-primary">{fmt(totalDeductions)}</p>
          <p className="text-xs text-gray-400 mt-1">claimable this year</p>
        </div>

        {/* Needs review */}
        <div className={`border rounded-2xl p-5 ${uncategorised > 0 ? "bg-amber-50 border-amber-200" : "bg-white border-gray-200"}`}>
          <div className="flex items-center gap-2 mb-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${uncategorised > 0 ? "bg-amber-100" : "bg-gray-100"}`}>
              <AlertCircle size={16} className={uncategorised > 0 ? "text-amber-500" : "text-gray-400"} />
            </div>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Needs Review</span>
          </div>
          <p className={`text-2xl font-bold ${uncategorised > 0 ? "text-amber-700" : "text-gray-400"}`}>
            {uncategorised}
          </p>
          <p className="text-xs text-gray-400 mt-1">uncategorised transactions</p>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Link
          to="/upload"
          className="flex items-center gap-3 bg-primary text-white rounded-xl px-4 py-3.5 font-semibold text-sm hover:bg-primary-dark transition"
        >
          <Upload size={18} />
          Upload statement
          <ArrowRight size={14} className="ml-auto" />
        </Link>
        {uncategorised > 0 && (
          <Link
            to="/quick-sort"
            className="flex items-center gap-3 bg-amber-500 text-white rounded-xl px-4 py-3.5 font-semibold text-sm hover:bg-amber-600 transition"
          >
            <Zap size={18} />
            Quick Sort ({uncategorised})
            <ArrowRight size={14} className="ml-auto" />
          </Link>
        )}
        <Link
          to="/reports"
          className="flex items-center gap-3 bg-white border border-gray-200 text-gray-700 rounded-xl px-4 py-3.5 font-semibold text-sm hover:bg-gray-50 transition"
        >
          View reports
          <ArrowRight size={14} className="ml-auto" />
        </Link>
      </div>

      {/* Recent transactions */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900">Recent transactions</h2>
          <Link to="/transactions" className="text-xs text-primary font-medium hover:underline">
            View all
          </Link>
        </div>

        {recent.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <p className="text-gray-400 text-sm">No transactions yet.</p>
            <Link to="/upload" className="mt-3 inline-block text-sm text-primary font-medium hover:underline">
              Upload your first statement →
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {recent.map((tx) => {
              const id = tx.id || tx._id;
              return (
                <div key={id} className="flex items-center gap-3 px-5 py-3.5">
                  <div
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ background: tx.type === "credit" ? "#059669" : "#EF4444" }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {tx.merchant || tx.description}
                    </p>
                    <p className="text-xs text-gray-400">
                      {formatDate(tx.date)} ·{" "}
                      {tx.category === "uncategorized"
                        ? "⚠ Needs review"
                        : (tx.category || "").replace(/_/g, " ")}
                    </p>
                  </div>
                  <div
                    className="text-sm font-bold flex-shrink-0 ml-4"
                    style={{ color: tx.type === "credit" ? "#059669" : "#111827" }}
                  >
                    {tx.type === "credit" ? "+" : "-"}{fmt(tx.amount)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
